import { sendInviteEmails } from '@api/cadFile.js';
import { shareFolder } from '@api/projects.js';
import Popover from '@components/Common/Popover.js';
import toast from '@components/Common/Toast.js';
import { DropdownIcon } from '@icons/index.js';
import { UIButton, UIDiv, UIInput, UISpan, UIText } from '@ui';
import { validateEmail } from '@utils/validation.js';
import EmailAutocomplete from './EmailAutocomplete.js';

export default class EmailInput {
  constructor(options = {}) {
    this.accessControl = options.accessControl || false;
    this.emails = options.emails || [];
    this.invitedEmailsList = options.invitedEmailsList || [];
    this.resourceId = options.resourceId || '';
    this.hasOrg = options.hasOrg || false;
    this.isPublic = options.isPublic || false;
    this.isProject = options.isProject || false;
    this.orgUsers = options.orgUsers || [];
    this.onEmailsChange = options.onEmailsChange || (() => {});
    this.onSendInvitesComplete = options.onSendInvitesComplete || (() => {});
    this.loading = false;
    this.currentUserEmail = options.currentUserEmail || '';
    this.emailAutocomplete = null;
  }

  render() {
    const container = new UIDiv();
    container.setClass('mb-6');

    const emailInputContainer = new UIDiv();
    emailInputContainer.setClass('relative');

    const emailInputWrapper = new UIDiv();
    emailInputWrapper.setClass(
      'flex flex-col sm:flex-row items-start sm:items-center gap-2 rounded-xl p-2 min-h-[65px] transition-all bg-white border border-[var(--o-hive-primary-40)]',
    );

    const leftSection = new UIDiv();
    leftSection.setClass(
      'flex-1 w-full sm:w-auto flex flex-wrap items-center gap-1',
    );

    const emailChipsContainer = new UIDiv();
    emailChipsContainer.setClass('flex flex-wrap gap-1 w-full sm:w-auto');
    this.emailChipsContainer = emailChipsContainer;

    const emailAutocomplete = new EmailAutocomplete({
      orgUsers: this.orgUsers,
      currentUserEmail: this.currentUserEmail,
      excludeEmails: [
        ...this.emails.map(e => e.email.toLowerCase()),
        ...this.invitedEmailsList.map(e => e.email?.toLowerCase()),
      ],
      onSelect: user => {
        this.addEmail(user.email);
      },
      onEnterWithoutSelection: email => {
        return this.addEmail(email);
      },
    });
    this.emailAutocomplete = emailAutocomplete;

    leftSection.add(emailChipsContainer, emailAutocomplete.render());

    const rightSection = new UIDiv();
    rightSection.setClass(
      'flex items-center gap-2 w-full sm:w-auto flex-shrink-0',
    );

    const accessLevelButton = new UIButton();
    accessLevelButton.setClass(
      'invitedUser-select flex items-center gap-2 bg-gray-100 hover:bg-gray-200 py-1 px-3 rounded transition-all duration-300 w-full sm:w-auto justify-between sm:justify-start',
    );

    const currentAccessLevel = new UIText(
      i18next.t('menubar.share.option_member'),
    );
    currentAccessLevel.setClass('email-input-access-level-text');

    const dropdownArrow = new DropdownIcon({ color: 'black', size: 10 });

    accessLevelButton.add(currentAccessLevel, dropdownArrow);

    const inviteRoleOptions = [];
    if (this.accessControl.hasPermission('invite_admin')) {
      inviteRoleOptions.push('Admin');
    }
    if (this.accessControl.hasPermission('invite_manager')) {
      inviteRoleOptions.push('Manager');
    }
    if (this.accessControl.hasPermission('invite_member')) {
      inviteRoleOptions.push('Member');
    }

    const popoverContent = new UIDiv();
    const updatePopoverContent = () => {
      popoverContent.dom.innerHTML = '';
      inviteRoleOptions.forEach(level => {
        const option = new UIButton();
        option.setClass(
          'w-full rounded-md text-left px-4 py-2 hover:bg-gray-100 flex items-center justify-between',
        );

        const optionText = new UIText(
          i18next.t(`menubar.share.option_${level.toLowerCase()}`),
        );
        const checkmark = new UIDiv();

        if (currentAccessLevel.dom.textContent === level) {
          checkmark.setClass('w-4 h-4 text-[var(--o-hive-primary-100)]');
          checkmark.setTextContent('✓');
        }

        option.add(optionText, checkmark);
        option.onClick(() => {
          currentAccessLevel.setTextContent(level);
          updatePopoverContent();
          Popover.hide();
        });

        popoverContent.add(option);
      });
    };

    updatePopoverContent();

    accessLevelButton.onClick(e => {
      e.stopPropagation();
      Popover.show({
        trigger: accessLevelButton,
        content: popoverContent,
        placement: 'bottom-start',
        hideOnSelect: false,
      });
    });

    const sendInvitesButton = new UIButton(i18next.t('menubar.share.invite'));
    sendInvitesButton.setClass(
      'email-btn bg-[var(--o-hive-primary-100)] hover:opacity-80 text-white py-1 px-4 rounded-2xl text-md transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto',
    );
    sendInvitesButton.onClick(() => this.sendInvites());

    rightSection.add(accessLevelButton, sendInvitesButton);

    emailInputWrapper.add(leftSection, rightSection);
    emailInputContainer.add(emailInputWrapper);
    container.add(emailInputContainer);

    this.updateEmailChips(emailChipsContainer);

    return container;
  }

  addEmail(email) {
    if (!validateEmail(email)) {
      toast.warning(
        i18next.t('menubar.share.toast.invalid_email_format', {
          email,
        }),
      );
      return false;
    }

    const normalizedEmail = email.toLowerCase();

    // in pending emails
    if (this.emails.some(e => e.email.toLowerCase() === normalizedEmail)) {
      toast.warning(
        i18next.t('menubar.share.toast.email_already_in_pending_list', {
          email,
        }),
      );
      return false;
    }

    // in invited emails list
    if (
      this.invitedEmailsList.some(
        e => e.email.toLowerCase() === normalizedEmail,
      )
    ) {
      toast.warning(
        i18next.t('menubar.share.toast.email_already_invited', {
          email,
        }),
      );
      return false;
    }

    if (normalizedEmail === this.currentUserEmail) {
      toast.warning(
        i18next.t('menubar.share.toast.cannot_invite_yourself_to_resource', {
          email,
        }),
      );
      return false;
    }

    const selectedLevel = document.querySelector(
      '.email-input-access-level-text',
    ).textContent;
    const newEmail = {
      email,
      accessLevel: { level: selectedLevel },
      localStored: true,
    };

    this.emails.push(newEmail);
    this.onEmailsChange(this.emails);
    this.addEmailChip(this.emailChipsContainer, newEmail);

    if (this.emailAutocomplete) {
      this.emailAutocomplete.excludeEmails = [
        ...this.emails.map(e => e.email.toLowerCase()),
        ...this.invitedEmailsList.map(e => e.email?.toLowerCase()),
      ];
    }

    return true;
  }

  addEmailChip(container, email) {
    if (!container) return;

    const emailChip = new UIDiv();
    emailChip.setClass(
      'inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-sm rounded-full px-3 py-1 opacity-0 transform -translate-x-2 transition-all duration-300',
    );

    const emailText = new UISpan();
    emailText.setClass('max-w-[200px] truncate');
    emailText.setTextContent(email.email);

    const removeButton = new UIButton('×');
    removeButton.setClass(
      'ml-1 text-gray-500 hover:text-gray-700 font-medium text-lg leading-none focus:outline-none',
    );

    emailChip.add(emailText, removeButton);

    const makeEditable = () => {
      const editInput = new UIInput(email.email);
      editInput.setClass(
        'bg-transparent border-none outline-none text-sm w-full max-w-[200px]',
      );
      emailText.dom.style.display = 'none';
      emailChip.dom.insertBefore(editInput.dom, removeButton.dom);
      editInput.dom.focus();
      editInput.dom.select();
      return editInput;
    };

    const revertEditable = input => {
      if (emailChip.contains(input.dom)) {
        emailChip.dom.removeChild(input.dom);
      }
      emailText.dom.style.display = '';
    };

    const updateEmail = newValue => {
      if (!newValue || newValue === email.email) return false;

      const currentEmails = this.emails.filter(e => e.email !== email.email);
      const normalizedNewValue = newValue.toLowerCase();

      if (
        currentEmails.some(e => e.email.toLowerCase() === normalizedNewValue)
      ) {
        toast.warning(
          i18next.t('menubar.share.toast.email_already_in_pending_list', {
            email: newValue,
          }),
        );
        return false;
      }

      if (
        this.invitedEmailsList.some(
          e => e.email.toLowerCase() === normalizedNewValue,
        )
      ) {
        toast.warning(
          i18next.t('menubar.share.toast.email_already_invited', {
            email: newValue,
          }),
        );
        return false;
      }

      if (normalizedNewValue === this.currentUserEmail.toLowerCase()) {
        toast.warning(
          i18next.t('menubar.share.toast.cannot_invite_yourself_to_resource', {
            email: newValue,
          }),
        );
        return false;
      }

      if (!validateEmail(newValue)) {
        toast.warning(
          i18next.t('menubar.share.toast.invalid_email_format', {
            email: newValue,
          }),
        );
        return false;
      }

      const selectedLevel = document.querySelector(
        '.email-input-access-level-text',
      ).textContent;
      const newEmail = {
        email: newValue,
        accessLevel: { level: selectedLevel },
        localStored: true,
      };

      this.emails = [...currentEmails, newEmail];
      this.onEmailsChange(this.emails);
      container.dom.removeChild(emailChip.dom);
      this.addEmailChip(container, newEmail);

      if (this.emailAutocomplete) {
        this.emailAutocomplete.excludeEmails = [
          ...this.emails.map(e => e.email.toLowerCase()),
          ...this.invitedEmailsList.map(e => e.email?.toLowerCase()),
        ];
      }
      return true;
    };

    let isEditing = false;
    emailChip.dom.addEventListener('dblclick', e => {
      if (e.target === removeButton.dom || isEditing) return;
      isEditing = true;

      const input = makeEditable();
      const finishEditing = newValue => {
        if (!isEditing) return;
        isEditing = false;
        if (!updateEmail(newValue?.trim())) {
          revertEditable(input);
        }
      };

      input.onKeyDown(e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          finishEditing(input.getValue());
        } else if (e.key === 'Escape') {
          finishEditing(null);
        }
      });

      input.onBlur(() =>
        setTimeout(() => finishEditing(input.getValue()), 100),
      );
    });

    removeButton.onClick(() => {
      emailChip.dom.classList.add('opacity-0', '-translate-x-2');
      setTimeout(() => {
        this.emails = this.emails.filter(e => e.email !== email.email);
        this.onEmailsChange(this.emails);
        container.dom.removeChild(emailChip.dom);
      }, 300);
    });

    container.dom.appendChild(emailChip.dom);
    requestAnimationFrame(() =>
      emailChip.dom.classList.remove('opacity-0', '-translate-x-2'),
    );
  }

  updateEmailChips(container) {
    if (!container) return;
    container.dom.innerHTML = '';

    const emailList = this.emails.filter(p => p.localStored === true);
    emailList.forEach(email => {
      this.addEmailChip(container, email);
    });
  }

  async sendInvites() {
    if (!this.accessControl.hasPermission('update')) {
      toast.error(
        i18next.t(
          'menubar.share.toast.you_do_not_have_permission_to_invite_users',
        ),
      );
      return;
    }

    const pendingEmail = this.emailAutocomplete.getCurrentValue();
    if (pendingEmail && validateEmail(pendingEmail)) {
      this.addEmail(pendingEmail);
      this.emailAutocomplete.clearInput();
    }

    const emailsToInvite = this.emails.filter(
      email => email.localStored === true,
    );
    if (emailsToInvite.length === 0) {
      toast.error(
        i18next.t('menubar.share.toast.please_add_at_least_one_email'),
      );
      return;
    }

    const invalidEmails = emailsToInvite.filter(
      email => !validateEmail(email.email),
    );
    if (invalidEmails.length > 0) {
      toast.error(
        i18next.t('menubar.share.toast.invalid_emails', {
          emails: invalidEmails.map(e => e.email).join(', '),
        }),
      );
      return;
    }

    if (!this.hasOrg && this.isPublic) {
      toast.error(
        i18next.t('menubar.share.toast.cannot_invite_to_public_resource'),
      );
      return;
    }

    this.setLoadingState(true);
    toast.info(i18next.t('menubar.share.toast.sending_invites'));
    try {
      const selectedAccessLevel = document.querySelector(
        '.email-input-access-level-text',
      ).textContent;

      const invitationDetails = emailsToInvite.map(email => ({
        recipient: email.email,
        fullName: email.fullName,
        accessLevel: selectedAccessLevel,
        expiry: null,
      }));

      let response;
      if (this.isProject) {
        response = await shareFolder(this.resourceId, {
          invitationDetails,
        });
      } else {
        response = await sendInviteEmails(this.resourceId, invitationDetails);
      }

      if (response.status === 200) {
        this.emails = [];
        this.onEmailsChange(this.emails);
        this.updateEmailChips(this.emailChipsContainer);

        toast.success(i18next.t('menubar.share.toast.invites_sent'));
        await this.onSendInvitesComplete();
      } else {
        toast.error(i18next.t('menubar.share.toast.error_sending_invites'));
      }
    } catch (error) {
      console.error('Error sending invitations:', error);
      toast.error(
        error?.message ||
          error?.response?.message ||
          error?.response ||
          i18next.t('menubar.share.toast.error_sending_invites'),
      );
    } finally {
      this.setLoadingState(false);
    }
  }

  setLoadingState(isLoading) {
    this.loading = isLoading;
    const button = document.querySelector('.email-btn');
    if (!button) return;

    if (isLoading) {
      button.textContent = 'Sending...';
      button.disabled = true;
    } else {
      button.textContent = i18next.t('menubar.share.invite');
      button.disabled = false;
    }
  }
}
